import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAllCards } from '../../tarot-data/cards'
import { localizeCard } from '../../i18n/useLocalized'
import VoiceHalo from '../../voice/VoiceHalo'
import { cardImage, track } from './landingUtils'

// The three-card spread the sample narration reads: past, present, future
const SAMPLE_CARD_IDS = [9, 17, 22] // The Hermit, The Star, Ace of Wands

/**
 * Splits the transcript into one segment per spoken word, using the word
 * timings the TTS returned, so each segment can light up as it is said.
 * Punctuation and spaces ride along with the word before them.
 */
const buildSegments = (text, words) => {
    const lower = text.toLowerCase()
    const marks = []
    let cursor = 0
    for (const { word, start } of words) {
        const token = String(word || '').toLowerCase().trim()
        if (!token) continue
        const at = lower.indexOf(token, cursor)
        if (at === -1) continue
        marks.push({ at, start })
        cursor = at + token.length
    }
    if (!marks.length) return null
    marks[0].at = 0
    return marks.map((m, i) => ({
        text: text.slice(m.at, i + 1 < marks.length ? marks[i + 1].at : text.length),
        start: m.start,
    }))
}

/**
 * Loads /audio/sample-<lng>.json (word timings) beside its .mp3. Both are
 * generated once from the backend's own TTS (scripts/make-voice-sample.js).
 * Until they exist, the section shows the transcript without a play button.
 */
const useSample = (language) => {
    const [words, setWords] = useState(null)

    useEffect(() => {
        let cancelled = false
        setWords(null)
        fetch(`/audio/sample-${language}.json`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => { if (!cancelled && Array.isArray(data?.words)) setWords(data.words) })
            // A missing file comes back as the app's index.html, which fails to parse
            .catch(() => {})
        return () => { cancelled = true }
    }, [language])

    return words
}

const HearReading = () => {
    const { t, i18n } = useTranslation()
    const language = i18n.language === 'es' ? 'es' : 'en'
    const transcript = t('public.hear.transcript')
    const positions = t('public.hear.positions', { returnObjects: true })
    const words = useSample(language)
    const segments = useMemo(() => (words ? buildSegments(transcript, words) : null), [transcript, words])

    const audioRef = useRef(null)
    const graph = useRef(null)
    const [analyser, setAnalyser] = useState(null)
    const [playing, setPlaying] = useState(false)
    const [spoken, setSpoken] = useState(-1)

    const cards = useMemo(() => {
        const all = getAllCards()
        return SAMPLE_CARD_IDS.map((id) => localizeCard(all.find((c) => c.id === id), language))
    }, [language])

    // A new language means a new recording: stop and start over
    useEffect(() => {
        const audio = audioRef.current
        if (audio) audio.pause()
        setPlaying(false)
        setSpoken(-1)
    }, [language])

    // While playing, follow the audio clock frame by frame, re-rendering only
    // when the word being spoken changes
    useEffect(() => {
        if (!playing || !segments) return undefined
        let frame = 0
        const tick = () => {
            const now = audioRef.current?.currentTime ?? 0
            let index = -1
            while (index + 1 < segments.length && segments[index + 1].start <= now) index++
            setSpoken((prev) => (prev === index ? prev : index))
            frame = requestAnimationFrame(tick)
        }
        frame = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(frame)
    }, [playing, segments])

    const toggle = useCallback(async () => {
        const audio = audioRef.current
        if (!audio) return
        if (!audio.paused) {
            audio.pause()
            return
        }

        // The analyser feeds the halo. Built on the first press, since an
        // AudioContext may only start from a user gesture.
        if (!graph.current) {
            try {
                const Ctx = window.AudioContext || window.webkitAudioContext
                const ctx = new Ctx()
                const node = ctx.createAnalyser()
                node.fftSize = 1024
                ctx.createMediaElementSource(audio).connect(node)
                node.connect(ctx.destination)
                graph.current = ctx
                setAnalyser(node)
            } catch {
                graph.current = 'none' // Play the audio anyway, without the halo
            }
        }
        if (graph.current?.state === 'suspended') await graph.current.resume()

        if (audio.ended) audio.currentTime = 0
        try {
            await audio.play()
            track('sample_play', { language })
        } catch {
            setPlaying(false)
        }
    }, [language])

    useEffect(() => () => {
        if (graph.current && graph.current !== 'none') graph.current.close?.()
    }, [])

    const available = Boolean(segments)

    return (
        <section className="arcana-section hear" aria-labelledby="hear-heading">
            <div className="arcana__inner">
                <header className="arcana__section-head reveal">
                    <p className="arcana__eyebrow">{t('public.hear.eyebrow')}</p>
                    <h2 id="hear-heading" className="arcana__h2">{t('public.hear.heading')}</h2>
                    <p className="arcana__section-text">{t('public.hear.text')}</p>
                </header>

                <div className="hear__spread reveal">
                    {cards.map((card, i) => (
                        <figure key={card.id} className="hear__card">
                            <img
                                {...cardImage(card.filename, '(max-width: 560px) 28vw, 170px')}
                                alt={card.name}
                                width="320"
                                height="533"
                                loading="lazy"
                                decoding="async"
                            />
                            <figcaption>
                                <span className="hear__position">{Array.isArray(positions) ? positions[i] : ''}</span>
                                <span className="hear__card-name">{card.name}</span>
                            </figcaption>
                        </figure>
                    ))}
                </div>

                <div className="reveal">
                <div className={`hear__player ${available ? '' : 'is-text-only'}`}>
                    {available && (
                        <div className="hear__halo">
                            <VoiceHalo analyser={analyser} active={playing || spoken >= 0} playing={playing} />
                            <button
                                type="button"
                                className={`hear__play ${playing ? 'is-playing' : ''}`}
                                onClick={toggle}
                                aria-label={playing ? t('public.hear.pause') : t('public.hear.play')}
                            >
                                <span className="hear__play-icon" aria-hidden="true" />
                            </button>
                            <span className="hear__listen" aria-hidden="true">{t('public.hear.listen')}</span>
                        </div>
                    )}

                    <blockquote className="hear__transcript" lang={language}>
                        {available
                            ? segments.map((seg, i) => (
                                <span key={i} className={i <= spoken ? 'is-spoken' : undefined}>{seg.text}</span>
                            ))
                            : transcript}
                    </blockquote>

                    {available && (
                        <audio
                            ref={audioRef}
                            src={`/audio/sample-${language}.mp3`}
                            preload="none"
                            onPlay={() => setPlaying(true)}
                            onPause={() => setPlaying(false)}
                            onEnded={() => { setPlaying(false); setSpoken(segments.length - 1) }}
                        />
                    )}
                </div>
                </div>
            </div>
        </section>
    )
}

export default HearReading
